import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  executeAdminAction,
  getAdminUser,
  hasAnyPermission,
  planAdminAction,
} from "../api/adminClient";
import { QUESTION_TYPES, typeNeedsOptions } from "./questionTypeConfig";

const MAX_QUESTIONS = 40;
const MAX_PER_SECTION = 8;

const STARTERS = [
  "I want to evaluate manager coaching",
  "I want to evaluate customer onboarding, one flat list, 10 questions",
];

export function canUseSketch() {
  const user = getAdminUser();
  if (!user) return false;
  if (user.is_platform_admin) return true;
  const granted = user.permissions;
  if (!Array.isArray(granted) || granted.length === 0) return true;
  return (
    hasAnyPermission(["ai_copilot:use"]) &&
    hasAnyPermission(["assessment_form:manage", "survey_templates:manage"])
  );
}

export function DraftWithAiButton({ onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        className ||
        "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg border border-[#004F73]/30 text-[#004F73] hover:bg-[#004F73]/5 transition-colors"
      }
    >
      <style>{`
        @keyframes sketch-ai-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(1.18); }
        }
        .sketch-ai-mark { animation: sketch-ai-pulse 1.6s ease-in-out infinite; transform-origin: center; }
        @media (prefers-reduced-motion: reduce) {
          .sketch-ai-mark { animation: none; }
        }
      `}</style>
      <svg className="sketch-ai-mark h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M9.8 3.1c.22-.73 1.25-.73 1.47 0l.72 2.42c.14.48.51.85.99.99l2.42.72c.73.22.73 1.25 0 1.47l-2.42.72a1.6 1.6 0 0 0-.99.99l-.72 2.42c-.22.73-1.25.73-1.47 0l-.72-2.42a1.6 1.6 0 0 0-.99-.99l-2.42-.72c-.73-.22-.73-1.25 0-1.47l2.42-.72c.48-.14.85-.51.99-.99l.72-2.42z" />
        <path d="M17.15 13.35c.14-.46.78-.46.92 0l.4 1.34c.1.32.35.57.67.67l1.34.4c.46.14.46.78 0 .92l-1.34.4a1.05 1.05 0 0 0-.67.67l-.4 1.34c-.14.46-.78.46-.92 0l-.4-1.34a1.05 1.05 0 0 0-.67-.67l-1.34-.4c-.46-.14-.46-.78 0-.92l1.34-.4c.32-.1.57-.35.67-.67l.4-1.34z" opacity="0.8" />
      </svg>
      Draft with AI
    </button>
  );
}

function sectionTitle(section, index, layout) {
  const label = (section?.label || "").trim();
  if (label) return label;
  if (layout === "flat") return "Questions";
  return `Section ${index + 1}`;
}

function newQuestionId() {
  return `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function stampPlan(plan) {
  if (!plan) return plan;
  return {
    ...plan,
    sections: (plan.sections || []).map((section, sIndex) => ({
      ...section,
      questions: (section.questions || []).map((question, qIndex) => ({
        include_in_lite: false,
        question_type: "importance_agreement",
        text: "",
        ...question,
        options: Array.isArray(question.options)
          ? question.options.map((opt) => (typeof opt === "string" ? opt : opt?.label || ""))
          : [],
        _qid: question._qid || `q-${sIndex}-${qIndex}-${newQuestionId()}`,
      })),
    })),
  };
}

function questionCount(plan) {
  return (plan?.sections || []).reduce((n, section) => n + (section.questions || []).length, 0);
}

function sectionCap(plan) {
  return plan?.layout === "flat" ? MAX_QUESTIONS : MAX_PER_SECTION;
}

function draftProblem(plan) {
  const questions = (plan?.sections || []).flatMap((section) => section.questions || []);
  if (!questions.length) return "Add at least one question.";
  if (questions.some((question) => !String(question.text || "").trim())) {
    return "Every question needs text before you create the assessment.";
  }
  const missingOptions = questions.some((question) => {
    if (!typeNeedsOptions(question.question_type)) return false;
    const labels = (question.options || []).map((opt) => String(opt || "").trim()).filter(Boolean);
    return labels.length < 2;
  });
  if (missingOptions) return "Choice questions need at least 2 options.";
  return "";
}

function planForSave(plan) {
  return {
    ...plan,
    sections: (plan.sections || []).map((section) => ({
      label: section.label,
      include_in_default_lite: section.include_in_default_lite,
      questions: (section.questions || []).map((question) => ({
        text: String(question.text || "").trim(),
        question_type: question.question_type || "importance_agreement",
        include_in_lite: Boolean(question.include_in_lite),
        options: typeNeedsOptions(question.question_type)
          ? (question.options || []).map((opt) => String(opt || "").trim()).filter(Boolean)
          : [],
      })),
    })),
  };
}

function DraftCard({ plan, disabled, onChange, current = true }) {
  const sections = plan?.sections || [];
  const total = questionCount(plan);
  const perSection = sectionCap(plan);

  const updateQuestion = (sIndex, qIndex, patch) => {
    if (!onChange) return;
    onChange({
      ...plan,
      sections: sections.map((section, index) => {
        if (index !== sIndex) return section;
        return {
          ...section,
          questions: (section.questions || []).map((question, questionIndex) => {
            if (questionIndex !== qIndex) return question;
            return { ...question, ...patch };
          }),
        };
      }),
    });
  };

  const changeType = (sIndex, qIndex, questionType) => {
    const current = sections[sIndex]?.questions?.[qIndex] || {};
    const needsOptions = typeNeedsOptions(questionType);
    let options = current.options || [];
    if (!needsOptions) {
      options = [];
    } else if (options.filter((opt) => String(opt || "").trim()).length < 2 && options.length < 2) {
      options = ["", ""];
    }
    updateQuestion(sIndex, qIndex, { question_type: questionType, options });
  };

  const updateOption = (sIndex, qIndex, oIndex, value) => {
    const current = sections[sIndex]?.questions?.[qIndex]?.options || [];
    const options = current.map((opt, index) => (index === oIndex ? value : opt));
    updateQuestion(sIndex, qIndex, { options });
  };

  const addOption = (sIndex, qIndex) => {
    const current = sections[sIndex]?.questions?.[qIndex]?.options || [];
    updateQuestion(sIndex, qIndex, { options: [...current, ""] });
  };

  const removeOption = (sIndex, qIndex, oIndex) => {
    const current = sections[sIndex]?.questions?.[qIndex]?.options || [];
    updateQuestion(sIndex, qIndex, { options: current.filter((_, index) => index !== oIndex) });
  };

  const deleteQuestion = (sIndex, qIndex) => {
    onChange({
      ...plan,
      sections: sections.map((section, index) => {
        if (index !== sIndex) return section;
        return {
          ...section,
          questions: (section.questions || []).filter((_, questionIndex) => questionIndex !== qIndex),
        };
      }),
    });
  };

  const addQuestion = (sIndex) => {
    onChange({
      ...plan,
      sections: sections.map((section, index) => {
        if (index !== sIndex) return section;
        return {
          ...section,
          questions: [
            ...(section.questions || []),
            {
              _qid: newQuestionId(),
              text: "",
              question_type: "importance_agreement",
              include_in_lite: false,
              options: [],
            },
          ],
        };
      }),
    });
  };

  return (
    <div className="rounded-xl border border-[#004F73]/15 bg-white">
      <div className="px-4 py-3 border-b border-slate-100">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004F73]/70">
          {current ? "Draft" : "Earlier draft"}
        </p>
        <p className="text-sm font-semibold text-slate-900 mt-0.5">{plan.name}</p>
        {plan.description ? (
          <p className="text-xs text-slate-500 mt-1">{plan.description}</p>
        ) : null}
        <p className="text-[11px] text-slate-400 mt-1">
          {current
            ? "Edit questions here. Create assessment saves this version."
            : "Kept as it was. The latest draft is below."}
        </p>
      </div>
      <div className="px-4 py-3 space-y-4">
        {sections.map((section, index) => {
          const questions = section.questions || [];
          const canAdd = total < MAX_QUESTIONS && questions.length < perSection;
          return (
            <div key={`${section.label || "section"}-${index}`}>
              <p className="text-xs font-semibold text-slate-700 mb-2">
                {sectionTitle(section, index, plan.layout)}
              </p>
              <div className="space-y-2">
                {questions.map((question, qIndex) => (
                  <div
                    key={question._qid || `${index}-${qIndex}`}
                    className="rounded-lg border border-slate-200 bg-slate-50/70 p-2.5"
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-2 w-5 shrink-0 text-xs text-slate-400">{qIndex + 1}.</span>
                      <textarea
                        value={question.text || ""}
                        disabled={disabled}
                        rows={2}
                        onChange={(event) => updateQuestion(index, qIndex, { text: event.target.value })}
                        className="flex-1 border border-slate-200 rounded-lg px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/25 disabled:bg-slate-100"
                        aria-label={`Question ${qIndex + 1} text`}
                      />
                    </div>
                    <div className="mt-2 ml-7 flex flex-wrap items-center gap-2">
                      <select
                        value={question.question_type || "importance_agreement"}
                        disabled={disabled}
                        onChange={(event) => changeType(index, qIndex, event.target.value)}
                        className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white disabled:bg-slate-100"
                        aria-label={`Question ${qIndex + 1} type`}
                      >
                        {QUESTION_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      {question.include_in_lite ? (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#004F73]">
                          LITE
                        </span>
                      ) : null}
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => deleteQuestion(index, qIndex)}
                        className="ml-auto text-xs font-medium text-rose-600 hover:text-rose-700 disabled:opacity-40"
                      >
                        Delete
                      </button>
                    </div>
                    {typeNeedsOptions(question.question_type) ? (
                      <div className="mt-2 ml-7 space-y-1.5">
                        {(question.options || []).map((option, oIndex) => (
                          <div key={`${question._qid}-opt-${oIndex}`} className="flex items-center gap-2">
                            <input
                              value={option}
                              disabled={disabled}
                              onChange={(event) => updateOption(index, qIndex, oIndex, event.target.value)}
                              placeholder={`Option ${oIndex + 1}`}
                              className="flex-1 border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/25"
                              aria-label={`Question ${qIndex + 1} option ${oIndex + 1}`}
                            />
                            <button
                              type="button"
                              disabled={disabled || (question.options || []).length <= 2}
                              onClick={() => removeOption(index, qIndex, oIndex)}
                              className="text-xs text-slate-400 hover:text-slate-600 disabled:opacity-30"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => addOption(index, qIndex)}
                          className="text-xs font-medium text-[#004F73] hover:underline disabled:opacity-40"
                        >
                          Add option
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))}
                {questions.length === 0 ? (
                  <p className="text-xs text-slate-400">No questions in this section.</p>
                ) : null}
                <button
                  type="button"
                  disabled={disabled || !canAdd}
                  onClick={() => addQuestion(index)}
                  className="text-xs font-semibold text-[#004F73] hover:underline disabled:opacity-40 disabled:no-underline"
                >
                  Add question
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SketchModal({
  open,
  onClose,
  organizationId = "",
  onCreated,
  onOpenEditor,
  onUseManual,
}) {
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState(null);
  const [flowId, setFlowId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setThread([
      {
        kind: "message",
        role: "assistant",
        content:
          "What is this assessment about?\n\nWho is it for, and what do you need to learn from it?",
      },
    ]);
    setDraft(null);
    setFlowId(null);
    setPlan(null);
    setSuggestions([]);
    setInput("");
    setCreated(null);
    setLoading(false);
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [open, thread, created, loading]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  useEffect(() => {
    if (open && !created) inputRef.current?.focus();
  }, [open, created]);

  if (!open) return null;

  const send = async (text) => {
    const asked = String(text || "").trim();
    if (!asked || loading || saving || created) return;
    setInput("");
    const history = [
      ...thread
        .filter((item) => item.kind === "message")
        .map((item) => ({ role: item.role, content: item.content })),
      { role: "user", content: asked },
    ];
    setThread((prev) => [...prev, { kind: "message", role: "user", content: asked }]);
    setLoading(true);
    setSuggestions([]);
    try {
      const res = await planAdminAction({
        message: asked,
        draft,
        history,
        flowId,
        context: {
          surface: "sketch",
          organization_id: organizationId || undefined,
        },
      });
      const data = res?.data || {};
      if (data.flow_id) setFlowId(data.flow_id);
      const nextPlan = data.action === "preview_assessment" && data.plan ? stampPlan(data.plan) : null;
      if (data.draft || nextPlan) {
        setDraft({
          ...(data.draft || {}),
          ...(nextPlan ? { plan: nextPlan } : {}),
        });
      }
      if (nextPlan) {
        setPlan(nextPlan);
      } else if (
        (data.action === "clarify" || data.action === "unavailable") &&
        !thread.some((item) => item.kind === "draft")
      ) {
        setPlan(null);
      }
      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
      setThread((prev) => [
        ...prev,
        {
          kind: "message",
          role: "assistant",
          content: data.reply || "I need a bit more detail.",
        },
        ...(nextPlan ? [{ kind: "draft", plan: nextPlan }] : []),
      ]);
    } catch (err) {
      toast.error(err?.message || "Sketch could not continue that draft");
      setThread((prev) => [
        ...prev,
        {
          kind: "message",
          role: "assistant",
          content: err?.message || "Something went wrong. Try again, or use New assessment.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const applyPlan = (nextPlan) => {
    const stamped = stampPlan(nextPlan);
    const total = questionCount(stamped);
    setPlan(stamped);
    setThread((prev) => {
      const next = prev.slice();
      for (let index = next.length - 1; index >= 0; index -= 1) {
        if (next[index].kind === "draft") {
          next[index] = { ...next[index], plan: stamped };
          break;
        }
      }
      return next;
    });
    setDraft((prev) => {
      if (!prev) return { flow_kind: "create_assessment", awaiting: "confirm", plan: stamped };
      const brief = { ...(prev.brief || {}) };
      if (total > 0) brief.question_count = total;
      return { ...prev, plan: stamped, brief };
    });
  };

  const createAssessment = async () => {
    if (!plan || saving) return;
    const problem = draftProblem(plan);
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    try {
      const res = await executeAdminAction({
        flow_id: flowId || undefined,
        plan: {
          ...planForSave(plan),
          action: "create_assessment",
          organization_id: organizationId || plan.organization_id || undefined,
        },
      });
      const data = res?.data || {};
      setCreated({
        ...data,
        message: res?.message || data.message || "Assessment created.",
      });
      setPlan(null);
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.message || "Could not create the assessment");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sketch-title"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100 bg-[#004F73]/[0.04]">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004F73]/70">
              Studio
            </p>
            <h3 id="sketch-title" className="text-lg font-semibold text-slate-900">
              Sketch
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              Draft an assessment from a short description. New assessment is still there if you want to build it by hand.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none w-8 h-8 disabled:opacity-40"
            aria-label="Close Sketch"
          >
            ×
          </button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/60">
          {thread.map((item, index) => {
            if (item.kind === "draft") {
              const current = thread.slice(index + 1).every((later) => later.kind !== "draft");
              return (
                <DraftCard
                  key={`draft-${index}`}
                  plan={item.plan}
                  current={current}
                  disabled={loading || saving || !current}
                  onChange={current ? applyPlan : undefined}
                />
              );
            }
            return (
              <div
                key={`${item.role}-${index}`}
                className={`flex ${item.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                    item.role === "user"
                      ? "bg-[#004F73] text-white"
                      : "bg-white border border-slate-200 text-slate-700"
                  }`}
                >
                  {item.content}
                </div>
              </div>
            );
          })}
          {loading ? (
            <p className="text-xs text-slate-400">Sketch is thinking…</p>
          ) : null}
          {created ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              <p>{created.message}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onOpenEditor?.(created.template_id)}
                  className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-[#004F73] hover:opacity-90"
                >
                  Open in editor
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-200 bg-white text-slate-700"
                >
                  Close
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {created ? null : (
          <div className="border-t border-slate-100 px-5 py-3 space-y-2 bg-white">
            {suggestions.length > 0 && !loading ? (
              <div className="flex flex-wrap gap-2">
                {suggestions.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => send(chip)}
                    className="px-2.5 py-1 text-xs font-medium rounded-full border border-[#004F73]/25 text-[#004F73] hover:bg-[#004F73]/5"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            ) : null}
            {thread.filter((item) => item.kind === "message").length <= 1 && !loading ? (
              <div className="flex flex-wrap gap-2">
                {STARTERS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => send(chip)}
                    className="px-2.5 py-1 text-xs font-medium rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            ) : null}
            <form
              className="flex items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                send(input);
              }}
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="What is this assessment about?"
                disabled={loading || saving}
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/25 disabled:bg-slate-50"
              />
              <button
                type="submit"
                disabled={loading || saving || !input.trim()}
                className="px-3 py-2.5 text-sm font-semibold rounded-lg text-white bg-[#004F73] disabled:opacity-40"
              >
                Send
              </button>
            </form>
            {plan ? (
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <button
                  type="button"
                  onClick={onUseManual}
                  className="text-xs font-medium text-slate-500 hover:text-slate-700"
                >
                  Use New assessment instead
                </button>
                <button
                  type="button"
                  onClick={createAssessment}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-semibold rounded-lg text-white bg-[#004F73] hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? "Creating…" : "Create assessment"}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={onUseManual}
                className="text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                Use New assessment instead
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
